import { Children, cloneElement, isValidElement } from "react";
import { cn } from "@/lib/utils";

interface FormFieldProps {
  /** Label text for the form field */
  label: string;
  /** HTML id of the input element (used for the label's htmlFor attribute) */
  htmlFor?: string;
  /** Show a red asterisk next to the label */
  required?: boolean;
  /** Error message displayed below the input in red */
  error?: string;
  /** Help text displayed below the input in muted color */
  helpText?: string;
  /** The input component to render inside the field */
  children: React.ReactNode;
  /** Additional class names for the outer wrapper */
  className?: string;
  /**
   * "compact" for dense admin layouts (a table's row details): a small muted label and small
   * messages that sit under the row's own title instead of outgrowing it.
   */
  size?: "default" | "compact";
}

const LABEL_CLASS = {
  default: "block text-body font-medium text-foreground mb-1.5",
  compact: "block text-xs font-medium text-foreground-muted mb-1",
} as const;
const MESSAGE_CLASS = {
  default: "text-body-sm mt-1.5",
  compact: "text-xs mt-1",
} as const;

/**
 * FormField - Wrapper that composes label, input, error message, and help text
 *
 * Features:
 * - Label with optional red asterisk for required fields
 * - Children slot for any input component (Input, Textarea, Select, etc.)
 * - Automatically injects aria-describedby and aria-invalid onto the child input
 * - Error message below input (red text with animate-shake on the wrapper)
 * - Help text below input in muted color
 * - Consistent spacing: label has mb-1.5, error/help has mt-1.5
 *
 * @example
 * <FormField label="Email" htmlFor="email" required error={errors.email}>
 *   <Input id="email" type="email" />
 * </FormField>
 */
export function FormField({
  label,
  htmlFor,
  required = false,
  error,
  helpText,
  children,
  className,
  size = "default",
}: FormFieldProps) {
  const errorId = htmlFor ? `${htmlFor}-error` : undefined;
  const helpId = htmlFor ? `${htmlFor}-help` : undefined;
  const describedBy =
    [error && errorId, helpText && !error && helpId].filter(Boolean).join(" ") || undefined;

  // Inject aria-describedby and aria-invalid into the child input element
  const enhancedChildren = Children.map(children, (child) => {
    if (isValidElement(child)) {
      const ariaProps: Record<string, unknown> = {};
      if (describedBy) ariaProps["aria-describedby"] = describedBy;
      if (error) ariaProps["aria-invalid"] = true;
      return cloneElement(child, ariaProps);
    }
    return child;
  });

  return (
    <div className={cn("w-full", error && "animate-shake", className)}>
      <label htmlFor={htmlFor} className={LABEL_CLASS[size]}>
        {label}
        {required && (
          <span className="text-red-500 ml-0.5" aria-hidden="true">
            *
          </span>
        )}
        {required && <span className="sr-only"> (Pflichtfeld)</span>}
      </label>
      {enhancedChildren}
      {error && (
        <p id={errorId} className={cn(MESSAGE_CLASS[size], "text-red-500")} role="alert">
          {error}
        </p>
      )}
      {helpText && !error && (
        <p id={helpId} className={cn(MESSAGE_CLASS[size], "text-foreground-muted")}>
          {helpText}
        </p>
      )}
    </div>
  );
}
